import { Link } from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";
import { DogCard } from "@/features/listings";
import { captureMoreDogsClicked } from "./more-dogs-analytics";
import type { ProviderOtherDogs } from "./use-provider-other-dogs";

const SHOWN = 6;

export function MoreDogsGrid({
    providerId,
    providerKind,
    otherDogs,
    sourceListingId,
}: {
    providerId: string;
    providerKind: "breeder" | "shelter";
    otherDogs: ProviderOtherDogs;
    sourceListingId: string;
}) {
    const posthog = usePostHog();
    if (otherDogs.status !== "ready") return null;
    return (
        <section aria-labelledby="more-dogs-grid-heading" className="mt-10">
            <div className="mb-4 flex items-baseline justify-between gap-4">
                <h2
                    id="more-dogs-grid-heading"
                    className="text-section-title text-doberman-black text-balance"
                >
                    {providerKind === "breeder"
                        ? "More from this breeder"
                        : "More from this rescue"}
                </h2>
                <Link
                    to="/provider/$providerId"
                    params={{ providerId }}
                    aria-label={`See all ${otherDogs.availableOthers.toLocaleString()} dogs from this ${providerKind === "breeder" ? "breeder" : "rescue"}`}
                    onClick={() =>
                        captureMoreDogsClicked(posthog, {
                            kind: "see_all",
                            surface: "other_dogs_grid",
                            sourceListingId,
                        })
                    }
                    className="flex min-h-11 shrink-0 items-center rounded-label px-1 text-label text-doberman-black/70 tabular-nums hover:text-doberman-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-doberman-black"
                >
                    See all {otherDogs.availableOthers.toLocaleString()}
                </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {otherDogs.dogs.slice(0, SHOWN).map((dog, position) => (
                    <DogCard
                        key={dog.id}
                        id={dog.id}
                        name={dog.name}
                        imageUrl={dog.imageUrl ?? ""}
                        breedNames={dog.breedName ? [dog.breedName] : []}
                        status="available"
                        variant="overlay"
                        measurementSurface="more_dogs_grid"
                        onOpen={() =>
                            captureMoreDogsClicked(posthog, {
                                kind: "dog",
                                surface: "other_dogs_grid",
                                position,
                                sourceListingId,
                                targetListingId: dog.id,
                            })
                        }
                    />
                ))}
            </div>
        </section>
    );
}
